import { Link, useNavigate } from 'react-router-dom';
import { IconChevronRight, IconHome } from '@tabler/icons-react';
import useTheme, { Theme } from 'chat-ui/src/hooks/useTheme';
import { cn } from '@/lib/utils';
import { getCategoryForPath, findNavItemByPath } from '../utils/docNavigation';

/**
 * Props for the Breadcrumbs component
 */
interface BreadcrumbsProps {
  /** Organization ID for navigation links */
  organizationId: string;
  /** Current document path */
  docPath: string;
  /** Optional current page title (overrides auto-detection) */
  currentTitle?: string;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Breadcrumb item component
 */
function BreadcrumbItem({
  href,
  label,
  isLast = false,
  currentTheme,
}: {
  href?: string;
  label: string;
  isLast?: boolean;
  currentTheme: Theme | null;
}) {
  return (
    <>
      {href ? (
        <Link
          to={href}
          className={cn(
            'hover:underline transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-1 rounded px-1',
            'text-sm md:text-base',
          )}
          style={{
            color: currentTheme?.primary || '#096846',
          }}
          aria-label={`Navigate to ${label}`}
        >
          {label}
        </Link>
      ) : (
        <span
          className={cn(
            'text-sm md:text-base',
            isLast && 'font-semibold truncate max-w-xs md:max-w-md',
          )}
          style={{
            color: isLast
              ? currentTheme?.['primary-text'] || '#111827'
              : currentTheme?.['secondary-text'] || '#6b7280',
          }}
          aria-current={isLast ? 'page' : undefined}
        >
          {label}
        </span>
      )}
      {!isLast && (
        <IconChevronRight
          size={16}
          className="mx-1 md:mx-2 flex-shrink-0"
          style={{ color: currentTheme?.['secondary-text'] || '#9ca3af' }}
          aria-hidden="true"
        />
      )}
    </>
  );
}

/**
 * Breadcrumbs component
 *
 * @description
 * Displays navigation breadcrumbs showing the current location in the documentation
 * hierarchy. Shows: Home > Documentation > Category > Page Title
 *
 * Features:
 * - Shows full navigation path
 * - Links to navigate back through hierarchy
 * - Auto-detects category and page title from docPath
 * - Theme-aware styling
 * - Responsive design (truncates on mobile)
 * - Keyboard accessible
 * - Screen reader friendly with proper ARIA attributes
 * - Visual separators using chevron icons
 *
 * Uses the getCategoryForPath and findNavItemByPath utilities to automatically
 * determine the navigation hierarchy based on the current document path.
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { organization_id, docPath } = useParams();
 *
 *   return (
 *     <div>
 *       <Breadcrumbs
 *         organizationId={organization_id}
 *         docPath={docPath}
 *       />
 *       {/* page content *\/}
 *     </div>
 *   );
 * }
 * ```
 */
export function Breadcrumbs({
  organizationId: _organizationId,
  docPath,
  currentTitle,
  className,
}: BreadcrumbsProps) {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();

  // Get category and page info from docPath
  const category = getCategoryForPath(docPath);
  const navItem = findNavItemByPath(docPath);
  const pageTitle = currentTitle || navItem?.title || 'Document';

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center py-3', className)}>
      <ol
        className="flex items-center flex-wrap gap-1"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {/* Home */}
        <li
          className="flex items-center"
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={cn(
              'hover:opacity-70 transition-opacity',
              'focus:outline-none focus:ring-2 focus:ring-offset-1 rounded p-1',
            )}
            style={{
              color: currentTheme?.primary || '#096846',
            }}
            aria-label="Go back to dashboard"
            itemProp="item"
          >
            <IconHome size={18} aria-hidden="true" />
            <meta itemProp="name" content="Dashboard" />
          </button>
          <meta itemProp="position" content="1" />
          <IconChevronRight
            size={16}
            className="mx-1 md:mx-2 flex-shrink-0"
            style={{ color: currentTheme?.['secondary-text'] || '#9ca3af' }}
            aria-hidden="true"
          />
        </li>

        {/* Documentation Root */}
        <li
          className="flex items-center"
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <BreadcrumbItem href="/documentation" label="Documentation" currentTheme={currentTheme} />
          <meta itemProp="position" content="2" />
        </li>

        {/* Category (if exists) */}
        {category && (
          <li
            className="flex items-center"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <BreadcrumbItem label={category} currentTheme={currentTheme} />
            <meta itemProp="position" content="3" />
          </li>
        )}

        {/* Current Page */}
        <li
          className="flex items-center min-w-0"
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <BreadcrumbItem label={pageTitle} isLast currentTheme={currentTheme} />
          <meta itemProp="position" content={category ? '4' : '3'} />
        </li>
      </ol>
    </nav>
  );
}
